import { bankLabel } from '../../utils/bank';
import React, { useEffect, useState } from 'react';
import SupervisorLayout from '../../components/SupervisorLayout';
import LotteryCodeField from '../../components/LotteryCodeField';
import SupervisorGroupCodeField from '../../components/SupervisorGroupCodeField';
import SupervisorBankCodeField from '../../components/SupervisorBankCodeField';
import DateField from '../../components/DateField';
import { fetchSupervisorWinningTickets } from '../../api/supervisor';
import { todayISO } from '../../utils/date';
import './SupervisorWinningTicketsPage.css';

const money = (n) => Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// Filtre Groupe (sous-groupe du superviseur) / Banque optionnel et
// mutuellement exclusif ajoute le 2026-09-17 (demande explicite) — meme
// pattern (resolution au blur + modal) que le reste du portail superviseur,
// vide = tout son perimetre.
const SupervisorWinningTicketsPage = () => {
  const [date, setDate] = useState(todayISO());
  const [tirage, setTirage] = useState('');
  const [tirageName, setTirageName] = useState('');
  const [groupId, setGroupId] = useState('');
  const [groupName, setGroupName] = useState('');
  const [bankId, setBankId] = useState('');
  const [bankName, setBankName] = useState('');
  const [data, setData] = useState({ rows: [], count: 0, grand_gain: '0.00' });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [expandedId, setExpandedId] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError('');
    fetchSupervisorWinningTickets({ date, tirage: tirage || undefined, group: groupId, bank: bankId })
      .then((d) => { if (!cancelled) setData(d); })
      .catch(() => { if (!cancelled) setError('Impossible de charger les tickets gagnants.'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [date, tirage, groupId, bankId]);

  return (
    <SupervisorLayout>
      <h2 className="svwt-title">Tickets Gagnant</h2>

      <div className="svwt-filters">
        <div className="svwt-row">
          <label className="svwt-label">Date</label>
          <DateField className="svwt-input" value={date} onChange={setDate} />
        </div>
        <div className="svwt-row">
          <label className="svwt-label">Loterie</label>
          <LotteryCodeField
            value={tirage}
            name={tirageName}
            onChange={(code) => { setTirage(code); setTirageName(''); }}
            onResolve={(code, nm) => { setTirage(code); setTirageName(nm); }}
            inputClass="svwt-input svwt-input-sm"
            iconClass="svwt-icon-btn"
            nameClass="svwt-scope-name"
            placeholder="Tout"
          />
          {tirage && (
            <button type="button" className="svwt-clear-btn" onClick={() => { setTirage(''); setTirageName(''); }}>
              ✕
            </button>
          )}
        </div>
        <div className="svwt-row">
          <label className="svwt-label">Groupe</label>
          <SupervisorGroupCodeField
            value={groupId}
            name={groupName}
            onChange={(id) => { setGroupId(id); setGroupName(''); }}
            onResolve={(id, nm) => {
              setGroupId(id); setGroupName(nm);
              setBankId(''); setBankName('');
            }}
            inputClass="svwt-input svwt-input-sm"
            iconClass="svwt-icon-btn"
            nameClass="svwt-scope-name"
            placeholder="Tout"
          />
          {groupId && (
            <button type="button" className="svwt-clear-btn" onClick={() => { setGroupId(''); setGroupName(''); }}>
              ✕
            </button>
          )}

          <label className="svwt-label svwt-label-gap">Banque</label>
          <SupervisorBankCodeField
            value={bankId}
            name={bankName}
            onChange={(id) => { setBankId(id); setBankName(''); }}
            onResolve={(id, nm) => {
              setBankId(id); setBankName(nm);
              setGroupId(''); setGroupName('');
            }}
            inputClass="svwt-input svwt-input-sm"
            iconClass="svwt-icon-btn"
            nameClass="svwt-scope-name"
            placeholder="Tout"
          />
          {bankId && (
            <button type="button" className="svwt-clear-btn" onClick={() => { setBankId(''); setBankName(''); }}>
              ✕
            </button>
          )}
        </div>
      </div>

      {error && <p className="svwt-error">{error}</p>}
      {loading ? (
        <p>Chargement...</p>
      ) : (
        <>
          <p className="svwt-summary">
            {data.count} ticket(s) gagnant(s) — Gain total : <strong>{money(data.grand_gain)}</strong>
          </p>
          <div className="svwt-table-wrap">
          <table className="svwt-table">
            <thead>
              <tr>
                <th>Banque</th>
                <th>Loterie</th>
                <th>SQ / No</th>
                <th>Heure</th>
                <th>Mise</th>
                <th>Gain</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {data.rows.length === 0 && (
                <tr><td colSpan={7} className="svwt-empty">Aucun ticket gagnant pour cette date.</td></tr>
              )}
              {data.rows.map((t) => (
                <React.Fragment key={t.id}>
                  <tr>
                    <td>{bankLabel(t.bank_seq, t.bank_name)} ({t.bank_username})</td>
                    <td>{t.tirage_code} — {t.tirage_name}</td>
                    <td>{t.sq ?? '—'} / {t.no ?? '—'}</td>
                    <td>{t.time}</td>
                    <td className="svwt-money">{money(t.total_amount)}</td>
                    <td className="svwt-money svwt-gain">{money(t.total_gain)}</td>
                    <td>
                      <span
                        className="svwt-toggle"
                        onClick={() => setExpandedId((cur) => (cur === t.id ? null : t.id))}
                      >
                        {expandedId === t.id ? '▲' : '▼'}
                      </span>
                    </td>
                  </tr>
                  {expandedId === t.id && (
                    <tr className="svwt-detail-row">
                      <td colSpan={7}>
                        <table className="svwt-lines-table">
                          <thead>
                            <tr><th>Produit</th><th>Numero</th><th>Mise</th><th>Gain</th></tr>
                          </thead>
                          <tbody>
                            {t.lines.map((ln, i) => (
                              <tr key={i}>
                                <td>{ln.product_abbr}</td>
                                <td>{ln.numbers}</td>
                                <td className="svwt-money">{money(ln.amount)}</td>
                                <td className="svwt-money">{money(ln.gain)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
          </div>
        </>
      )}
    </SupervisorLayout>
  );
};

export default SupervisorWinningTicketsPage;
